/**
 * useBinStatus - Bin status polling hook
 *
 * Fetches and polls bin status for the current location.
 * Auto-refreshes every 30 seconds to show current bin states.
 *
 * IMPORTANT: Uses `refetchIntervalInBackground: false` to pause polling
 * when browser tab is not visible. This is critical for:
 * - Reducing server load from inactive dashboard tabs
 * - Saving bandwidth on production floor TVs left open
 * - Battery life on mobile devices
 *
 * @returns TanStack Query result with bins data, loading, and error states
 */
import { trpc } from '@/lib/trpc';
import { useLocation } from '@/contexts/LocationContext';
import { skipToken } from '@tanstack/react-query';

export function useBinStatus() {
  const { selectedLocation } = useLocation();

  return trpc.bins.getLocationBins.useQuery(
    selectedLocation?.id
      ? { locationId: selectedLocation.id }
      : skipToken,
    {
      staleTime: 30_000,               // Consider fresh for 30s
      refetchInterval: 30_000,         // Poll every 30s (matches catcher/pretreat)
      refetchIntervalInBackground: false, // CRITICAL: Pause when tab hidden
    }
  );
}
